/**
 * Billing config flags — TanStack Query hooks (Prompt 11F.8).
 *
 * Endpoint: GET/PATCH /v1/config/billing.
 *
 * Deliberately narrow: only the two flags `payments.py` actually reads
 * (`auto_create_entitlements_on_paid`, `void_cascades_to_entitlements`).
 * GST rate now lives on `tax.default_gst_rate`
 * (`@features/settings/api/config`); the rest of the old mock Billing tab
 * (registration fee, invoice due days, late fee, auto-invoice generation)
 * has no backing engine yet — see the 11A.8 deferred backlog.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, tenantHeaderOverride } from '@core/api/client';
import { useTenant } from '@core/tenant/TenantContext';
import { useTenantSafeQuery } from '@core/tenant/useTenantSafeQuery';

export interface BillingConfig {
  flags: {
    auto_create_entitlements_on_paid: boolean;
    void_cascades_to_entitlements: boolean;
    [k: string]: boolean;
  };
}

export interface BillingConfigUpdate {
  auto_create_entitlements_on_paid?: boolean;
  void_cascades_to_entitlements?: boolean;
}

// Tenant id folded into the key — see the comment on `settingsConfigKeys`
// in @features/settings/api/config for why (queryClient.clear() alone does
// not reliably refetch an already-mounted useQuery on a tenant switch).
export const billingConfigKeys = {
  all: (tid: string) => ['config', 'billing', tid] as const,
};

export function useBillingConfig() {
  const { selectedTenantId, isAllMode } = useTenant();
  return useTenantSafeQuery({
    queryKey: billingConfigKeys.all(selectedTenantId),
    queryFn: () =>
      api.get<BillingConfig>(
        '/v1/config/billing',
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateBillingConfig() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: BillingConfigUpdate) =>
      api.patch<BillingConfig>(
        '/v1/config/billing',
        body,
        tenantHeaderOverride(selectedTenantId, isAllMode),
      ),
    onSuccess: (data) => qc.setQueryData(billingConfigKeys.all(selectedTenantId), data),
  });
}
